import PropTypes from 'prop-types';
import React, { useState, useEffect, useCallback } from 'react';

import { Search, XIcon } from 'lucide-react';
import { Input, Col, Row, Button, Label, Separator, List, ListItem, Checkbox, Badge, LoadingSpinner, cn } from 'twyne-new.ui'

const propTypes = {
  data: PropTypes.any,
  defaultValue: PropTypes.any,
  onChange: PropTypes.func,
};

export default class FilterConfigure extends React.Component {
  state = {
    suboptions: [],
    selectedField: null,
    selectedLabel: null,
    selected: {},
    isTerm: false,
    suboptionsLoading: false,
  }

  componentDidUpdate() {
    const filters = Object.values(this.props.defaultValue || {})
    const shouldUpdate = filters.some(filter => Array.isArray(filter))
    if (shouldUpdate) {
      this.parseState()
    }
  }

  componentDidMount() {
    this.parseState()
  }

  async parseState() {
    const { defaultValue, data: { options } } = this.props
    const filters = Object.entries(defaultValue || {})

    const getReportConfiguration = (options, filterKey) => {
      const option = options.find(({ value }) => value === filterKey)
      if (option) {
        const fetchSuboptions = option?.options
        const label = option?.label?.props?.children
        return { label, fetchSuboptions }
      }
      return null
    }

    const createItem = (suboptions, filterValue) => {
      const suboption = suboptions.find(({ value }) => value === filterValue)
      return {
        value: filterValue,
        label: suboption?.label,
        getValue: suboption?.getValue || (() => filterValue),
        getLabel: suboption?.getLabel || (() => suboption?.label),
      }
    }

    const selected = {}

    for (const [key, values] of filters) {
      if (!Array.isArray(values)) {
        this.setState({ selected: defaultValue })
        return;
      }

      if (!values.length) {
        continue;
      }

      const option = getReportConfiguration(options, key)
      if (!option) {
        continue;
      }

      const { fetchSuboptions } = option
      const suboptions = (typeof fetchSuboptions === 'function' ? await fetchSuboptions() : fetchSuboptions) || []
      const items = values.map(value => createItem(suboptions, value))
      selected[key] = { label: option.label, items }
    }

    this.setState({ selected }, () => this.props.onChange(selected))
  }

  handleChangeSelected = selected => {
    this.setState({ selected }, () => this.props.onChange(selected))
  }

  handleSelectField = async ({ label, options, value, term }) => {
    const selectedLabel = label.props && (label.props.children || '')
    this.loadingField = value
    this.setState({ suboptionsLoading: true })
    const suboptions = typeof options === 'function' ? await options() : options
    if (this.loadingField !== value) {
      return
    }
    this.setState({ suboptions: suboptions || [], selectedField: value, selectedLabel, isTerm: term, suboptionsLoading: false })
  }

  isValueSelected = value => {
    const { selected, selectedField, selectedLabel } = this.state
    const fieldData = selected[selectedField] = selected[selectedField] || { label: selectedLabel, items: [] }
    return !!fieldData.items.find(item => value == item.value)
  }

  handleSelectValue = (value, label, field, getValue) => action => {
    const { selected, selectedLabel } = this.state
    const fieldData = selected[field] = selected[field] || { label: selectedLabel, items: [] }

    if (action) {
      fieldData.items.push({ value, label, getValue })
    } else {
      const idx = fieldData.items.findIndex(item => value == item.value)
      idx !== -1 && fieldData.items.splice(idx, 1)
    }

    this.handleChangeSelected(selected)
  }

  ValueItem = ({ value, label, getValue, getLabel }) => <>
    <Checkbox
      onChange={this.handleSelectValue(value, label, this.state.selectedField, getValue)}
      value={this.isValueSelected(value)}
    />
    {getLabel()}
  </>

  PinItem = ({ onClose, children }) => {
    const handleClose = () => {
      onClose && onClose()
    }

    return <Badge variant='light' size='lg' className='hover:twy-bg-base'>
      <div className='flex justify-between items-center gap-2'>
        {children}
        <XIcon size='12' className='cursor-pointer' onClick={handleClose} />
      </div>
    </Badge>
  }

  SelectedValues = ({ field }) => {
    const { selected } = this.state
    return <div className='flex flex-wrap gap-2'>
      {selected[field].items.map((item, idx) => {
        const { value, label } = item
        const pinLabel = String(value)?.toLowerCase() === String(label)?.toLowerCase() || !label ? value : [`[#${value}]`, label].join(' - ')
        return <>
          <this.PinItem
            key={idx}
            onClose={this.handleSelectValue.call(this, value, label, field).bind(this, false)}
          >
            {pinLabel}
          </this.PinItem>
        </>
      })}
    </div>
  }

  handleClearFilter = () => {
    this.setState({ selected: {} }, () => this.props.onChange({}))
  }

  handleClearLabelFilter = selectedLabel => {
    const selectedLabelFields = Object.values(this.state.selected).find(item => item.label == selectedLabel)
    selectedLabelFields.items = []
    const selectedState = { ...this.state.selected, ...selectedLabelFields }
    this.setState({ selected: selectedState }, () => this.props.onChange({}))
  }

  handleSelectAll = (check, searchItems) => {
    const { selectedLabel, selectedField } = this.state

    this.handleClearLabelFilter(selectedLabel)
    for (const { value, label, getValue } of searchItems) {
      this.handleSelectValue(value, label, selectedField, getValue)(check)
    }
  }

  AppliedFilters = ({ onClearAll, selected }) => <>
    <div className='flex justify-between items-center mb-2'>
      <span className='twy-text-base-accent text-lg'>Applied Filters</span>
      <Button size='sm' onClick={onClearAll} variant='light' color='accent'>Clear all</Button>
    </div>
    <div className='flex flex-col gap-4 h-[200px] overflow-y-auto'>
      {Object.keys(selected).map((field, idx) => {
        const fieldLabel = selected[field] && selected[field].label
        if (selected[field] && selected[field].items && selected[field].items.length > 0) {
          return <div key={idx}>
            <Label className="twy-text-base-accent text-md mb-1">{fieldLabel}</Label>
            <this.SelectedValues field={field} />
          </div>
        }
      })}
    </div>
  </>

  render() {
    const { options } = this.props.data
    const { suboptions, selected } = this.state

    return <>
      <Row className='gap-0 max-md:[&>*:first-child]:col-span-5! max-md:[&>*:last-child]:col-span-7!'>
        <Col size='3' className='border-r twy-border-base pt-2 h-[calc(100vh-18rem)]'>
          <ListSearch
            items={options}
            onSelect={this.handleSelectField}
            height={600}
            searchClassName='pr-2'
          />
        </Col>
        <Col size='9'>
          <Row className='pt-2'>
            <Col>
              <ListSearch
                items={suboptions}
                itemRender={this.ValueItem}
                selected={this.state.selected}
                selectedLabel={this.state.selectedLabel}
                selectedField={this.state.selectedField}
                isTerm={this.state.isTerm}
                onSelectAll={this.handleSelectAll}
                extraItems={this.props.extraItems[this.state.selectedField]}
                onAddExtraItem={this.props.onAddExtraItem}
                checkboxList
                loading={this.state.suboptionsLoading}
                searchClassName='ml-2 pr-2'
              />
            </Col>
            <Col>
              <Separator className='mx-0 my-2' />
            </Col>
            <Col className='pl-4'>
              <this.AppliedFilters onClearAll={this.handleClearFilter} selected={selected} />
            </Col>
          </Row>
        </Col>
      </Row>
    </>
  }
}

function ListSearch({ items = [], onSelect, itemRender, checkboxList, selected, selectedLabel, onSelectAll, className, height = 350, searchClassName = '', isTerm, selectedField, onAddExtraItem, extraItems = [], loading = false }) {
  const [active, setActive] = useState(null)
  const [searchItems, setSearchItems] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [selectAll, setSelectAll] = useState(false)

  const syncExtraItems = useCallback(() => {
    const extraCount = searchItems.filter(item => item.extra).length
    if (extraItems.length !== extraCount) {
      const searchItemsCopy = [...searchItems].filter(item => !item.extra)
      const extraItemsSearch = extraItems.filter(item => item.match(new RegExp(searchTerm, 'gi')))
      for (const value of extraItemsSearch) {
        searchItemsCopy.push({ value, label: value, getValue: () => value, getLabel: () => value, extra: true })
      }
      setSearchItems(searchItemsCopy)
    }
  }, [extraItems, searchItems])

  useEffect(() => {
    if (extraItems && searchItems) {
      syncExtraItems()
    }
  }, [extraItems, selectedField, searchItems, syncExtraItems])

  const determineSelectedAll = () => {
    if (!selectedLabel) {
      return false
    }

    const selectedItems = Object.values(selected).find(item => item.label === selectedLabel)
    if (!selectedItems || !selectedItems.items || !selectedItems.items.length) {
      return false
    }

    const itemsCount = items.length + (!extraItems ? 0 : extraItems.length)
    if (itemsCount !== selectedItems.items.length) {
      return false
    }

    return true
  }

  const handleSelectAll = useCallback(check => {
    setSelectAll(check)
    onSelectAll(check, searchItems)
  }, [onSelectAll, searchItems])

  useEffect(() => {
    setSelectAll(determineSelectedAll())
  }, [JSON.stringify(selected), selectedLabel, extraItems])

  useEffect(() => {
    setActive(null)
    setSearchItems(items)
    setSearchTerm('')
  }, [items])

  useEffect(() => {
    setSearchItems(items.filter(({ label, value }) => {
      if (!searchTerm) return true

      const uiLabel = label.props ? label.props.children : (value !== label ? `[#${value}] - ${label}` : value)
      const rgx = new RegExp(searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi')

      if (uiLabel.match(rgx)) return true

      if (`${value} ${label}`.match(rgx)) return true

      return false
    }))
  }, [searchTerm, items, isTerm])

  const handleClick = item => () => {
    setActive(item.value)
    onSelect && onSelect(item)
  }

  const handleKeyDown = event => {
    if (event.key !== 'Enter' || searchItems.length !== 0) return

    event.preventDefault()
    setSearchTerm('')
    const parseSearch = searchTerm.replace(/[!(){}[\]^'"~:]/g, '');
    onAddExtraItem({ key: selectedField, value: parseSearch })
  }

  return <div className={className}>
    <SearchInput
      value={searchTerm}
      onChange={setSearchTerm}
      className={searchClassName}
      onKeyDown={event => isTerm && handleKeyDown(event)}
    />
    <Separator className='mx-0 my-2' />
    <div className='relative'>
      {loading && <div className='absolute top-20 left-0 right-0 z-40 flex justify-center pointer-events-none'>
        <LoadingSpinner />
      </div>}
      <div className={cn('overflow-y-auto', `h-[${height}px]`, loading && 'opacity-50')}>
        {checkboxList ? <>
          <ChildList
            searchItems={searchItems}
            active={active}
            onClick={handleClick}
            onClickAll={handleSelectAll}
            selectAll={selectAll}
            itemRender={itemRender}
            isTerm={isTerm}
          />
        </> : <>
          <ModuleList
            searchItems={searchItems}
            active={active}
            onClick={handleClick}
            itemRender={itemRender}
          />
        </>}
      </div>
    </div>
  </div>
}

const ModuleList = ({ searchItems, active, onClick, itemRender }) => <>
  <List className='px-0! pt-0!'>
    {searchItems.map((item, idx) => <ListItemModule key={idx} active={active === item.value} onClick={onClick(item)}>
      {itemRender ? itemRender(item) : item.label}
    </ListItemModule>)}
  </List>
</>

const ChildList = ({ searchItems, active, onClick, onClickAll, selectAll, itemRender, isTerm }) => <>
  {(searchItems.length > 0) && <>
    <ListItemChild
      onClick={onClickAll}
      value={selectAll}
      isSelectAll
    >
      Select all
    </ListItemChild>
  </>}

  {searchItems.map((item, idx) => <ListItemChild index={idx} key={idx} value={active === item.value} onClick={onClick(item)}>
    {itemRender ? itemRender(item) : item.label}
  </ListItemChild>)}

  {isTerm && searchItems.length === 0 ? <>
    <div className='py-2 px-4 twy-text-base-accent'>
      Press enter to create a custom filter
    </div>
  </> : null}
</>

const SearchInput = ({ value, onChange, className = '', onKeyDown }) => <>
  <div className={cn('relative w-full sticky top-0', className)}>
    <Input
      placeholder='Search...'
      className='pl-8 border-0 focus:bg-gray-200 focus:dark:bg-gray-950'
      value={value}
      onChange={e => onChange(e.target.value)}
      onKeyDown={onKeyDown}
    />
    <Search size='18' className='absolute left-2 top-1/2 -translate-y-1/2' />
  </div>
</>

const ListItemChild = ({ value, onClick, children, isSelectAll, index }) => <>
  <div className={cn('flex items-center gap-4 p-3', isSelectAll && 'border-b twy-border-base', index % 2 === 0 && 'twy-bg-base')}>
    {isSelectAll && <Checkbox onChange={onClick} value={value} />}
    {children}
  </div>
</>

const ListItemModule = ({ children, active, ...props }) => <>
  <ListItem
    className={cn('px-0 p-3 mr-2', active ? 'twy-subtle-neutral twy-text-base-accent' : '')}
    {...props}>
    {children}
  </ListItem>
</>

FilterConfigure.propTypes = propTypes
FilterConfigure.defaultProps = {}
